import { useState } from 'react';
import { cn } from '@/lib/utils';
import { getSenderPerspectiveLabel } from '@/lib/senderPerspective';
import { getBackdropPreset } from '@/lib/backdropPresets';
import { Heart, Lock, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import type { TemplateType } from '@/types/templates';
import type { CustomTemplateSettings, FloatingElement } from '@/types/announcement';
import { PhotoLightbox, EnlargeablePhoto } from '@/components/ui/photo-lightbox';
import templateLogo from '@/assets/sayitnow-template-logo.png';
import {
  ForeverInMyHeartBackground,
  ForeverInMyHeartOverlays,
  YourStoryMattersBackground,
  YourStoryMattersOverlays,
  OnceUponATimeBackground,
  OnceUponATimeOverlays,
  TheMomentBackground,
  TheMomentOverlays,
  ReflectionsOnAYearBackground,
  ReflectionsOnAYearOverlays,
  OrnamentalDivider,
} from './TemplateBackgrounds';

// ─── Shared types & helpers ──────────────────────────────────────

interface TemplateLayoutProps {
  recipientName: string;
  relationship: string;
  message: string;
  photoUrls?: string[];
  publishedDate?: string;
  customSettings?: CustomTemplateSettings;
  senderLabel?: string;
  messageRenderer?: (message: string, className: string) => React.ReactNode;
  metadata?: { chibiUrl?: string; [key: string]: any };
}

export const TEMPLATE_FIRST_LINES: Record<TemplateType, string> = {
  apology: "I've been wanting to say this for a while…",
  gratitude: "I don't say this enough, but…",
  hard_conversation: "There's something I need to share with you…",
  love: "I hope you know how much you mean to me…",
  closure: "I've been holding onto this, and it's time to let go…",
  custom: "There's something on my heart I want to share…",
  birthday: "On your special day, I want you to know…",
  anniversary: "Another year together, and I still feel…",
  celebration: "This moment deserves to be celebrated because…",
  encouragement: "I see how hard you've been working, and I want you to know…",
};

function TemplateBrandLogo() {
  return (
    <motion.div
      className="flex justify-center pt-10 pb-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.5 }}
    >
      <motion.img
        src={templateLogo}
        alt="Say It NOW"
        className="h-10 md:h-12 object-contain drop-shadow-md"
        animate={{
          filter: [
            'drop-shadow(0 0 4px rgba(16,185,129,0.15))',
            'drop-shadow(0 0 12px rgba(16,185,129,0.35))',
            'drop-shadow(0 0 4px rgba(16,185,129,0.15))',
          ],
        }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />
    </motion.div>
  );
}

function MessageBlock({
  message,
  className,
  messageRenderer,
}: {
  message: string;
  className: string;
  messageRenderer?: TemplateLayoutProps['messageRenderer'];
}) {
  if (messageRenderer) return <>{messageRenderer(message, className)}</>;
  const lines = message.split('\n');
  return (
    <>
      {lines.map((line, i) => (
        <p key={i} className={cn(className, line.trim() === '' ? 'h-4' : '')}>
          {line}
        </p>
      ))}
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// 1) LOVE — Cinematic Editorial Cover
//    Full-bleed hero image, gradient text overlay, magazine cover
// ════════════════════════════════════════════════════════════════
export function LoveLayout({
  recipientName, relationship, message, photoUrls, publishedDate, messageRenderer, senderLabel,
}: TemplateLayoutProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const heroPhoto = photoUrls?.[0];

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-rose-950 via-slate-900 to-slate-950">
      {heroPhoto ? (
        <div className="relative w-full" style={{ minHeight: '65vh' }}>
          <img
            src={heroPhoto} alt="Memory"
            className="w-full h-full object-cover absolute inset-0 cursor-pointer"
            onClick={() => setLightboxOpen(true)}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-slate-950" />
          <div className="absolute inset-0 bg-gradient-to-t from-transparent to-black/30" />

          {/* Magazine cover badge */}
          <motion.div className="absolute top-6 left-6 z-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <span className="text-[9px] uppercase tracking-[0.5em] text-white/50 font-medium border border-white/20 px-3 py-1">
              Love
            </span>
          </motion.div>

          {/* Cinematic headline over image bottom */}
          <motion.div
            className="absolute bottom-0 left-0 right-0 px-6 md:px-14 pb-10 z-10"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white leading-[0.95] tracking-tight drop-shadow-lg">
              I never said this…
              <br />
              <span className="font-bold italic text-rose-200">but you changed my life.</span>
            </h1>
            <p className="text-white/50 text-sm mt-4 font-sans uppercase tracking-widest">
              A message for {recipientName}
            </p>
          </motion.div>
        </div>
      ) : (
        <>
          <TheMomentBackground />
          <TheMomentOverlays />
          <motion.div className="relative z-10 pt-24 pb-12 px-6 md:px-14 text-center" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-[9px] uppercase tracking-[0.5em] text-rose-400/60 font-medium">Love</span>
            <h1 className="font-serif text-5xl md:text-6xl text-rose-100 mt-6 leading-[0.95]">For {recipientName}</h1>
          </motion.div>
        </>
      )}

      {/* Single-column editorial body */}
      <div className={cn('relative z-10 px-6 md:px-14 pb-12', heroPhoto && 'bg-gradient-to-b from-slate-950 to-slate-900')}>
        <div className="max-w-2xl mx-auto">
          {heroPhoto && (
            <motion.p className="text-xl md:text-2xl text-white/70 italic mt-10 mb-8 font-serif leading-relaxed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
              {recipientName}, there are some things I don&apos;t say often enough…
            </motion.p>
          )}
          <div className="w-20 h-px bg-rose-400/30 mx-auto my-8" />
          <div className="space-y-5">
            <MessageBlock
              message={message}
              className={cn('text-base md:text-lg leading-[1.85] font-sans', heroPhoto ? 'text-white/80' : 'text-rose-200/90 italic text-lg md:text-xl')}
              messageRenderer={messageRenderer}
            />
          </div>

          <motion.div className="mt-14 text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
            <div className="w-16 h-px bg-amber-400/40 mx-auto mb-4" />
            <p className={cn('text-lg italic font-serif', heroPhoto ? 'text-amber-200/70' : 'text-rose-300/80')}>
              {senderLabel ? `With all my love, ${senderLabel}` : 'With all my love'}
            </p>
            {publishedDate && <p className="text-sm mt-2 text-white/70">{publishedDate}</p>}
          </motion.div>
          <TemplateBrandLogo />
        </div>
      </div>

      {heroPhoto && <PhotoLightbox photos={[heroPhoto]} initialIndex={0} open={lightboxOpen} onOpenChange={setLightboxOpen} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 2) GRATITUDE — Editorial Feature Article (Newspaper/Magazine)
//    Masthead bar, two-column layout, pull-quote, sections
// ════════════════════════════════════════════════════════════════
export function GratitudeLayout({
  recipientName, relationship, message, photoUrls, publishedDate, messageRenderer, senderLabel,
}: TemplateLayoutProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const photos = photoUrls?.slice(0, 3) || [];
  const paragraphs = message.split('\n\n').filter(p => p.trim());
  const hasLongMessage = paragraphs.length > 2;

  return (
    <div className="min-h-screen relative overflow-hidden">
      <ForeverInMyHeartBackground />
      <ForeverInMyHeartOverlays />

      <div className="relative z-10">
        {/* ── Newspaper masthead ── */}
        <div className="border-b-2 border-amber-800/30 dark:border-amber-400/20 mx-4 md:mx-8">
          <div className="flex items-end justify-between py-2">
            <span className="text-[9px] uppercase tracking-[0.3em] text-amber-700/40 dark:text-amber-400/30 font-medium">
              Say It Now • Feature
            </span>
            {publishedDate && (
              <span className="text-[9px] uppercase tracking-wider text-amber-700/35 dark:text-amber-400/25">
                {publishedDate}
              </span>
            )}
          </div>
        </div>

        {/* ── Giant headline block ── */}
        <div className="px-6 md:px-12 pt-8 pb-4">
          <motion.h1
            className="font-serif text-5xl md:text-7xl lg:text-8xl text-amber-900 dark:text-amber-100 leading-[0.9] tracking-tight font-black"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            GRATITUDE
            <br />
            <span className="text-4xl md:text-5xl lg:text-6xl font-light tracking-wide">REPORT</span>
          </motion.h1>
          <div className="h-1 w-16 bg-amber-600/60 mt-4" />
        </div>

        {/* ── Two-column editorial body ── */}
        <div className="px-6 md:px-12 pb-10">
          <div className={cn('grid gap-8', photos.length > 0 ? 'md:grid-cols-[1fr_1.4fr]' : 'md:grid-cols-1 max-w-3xl')}>
            {/* Left column: photo + body overflow */}
            <div className="space-y-6">
              {photos[0] && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="rounded-lg overflow-hidden shadow-xl">
                  <EnlargeablePhoto src={photos[0]} alt="Memory" className="w-full aspect-[4/3] object-cover" onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }} />
                </motion.div>
              )}
              {hasLongMessage && paragraphs.slice(Math.ceil(paragraphs.length / 2)).map((p, i) => (
                <p key={i} className="text-sm md:text-base leading-[1.75] text-amber-800/75 dark:text-amber-200/75 font-sans">{p}</p>
              ))}
              {photos[1] && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="rounded-lg overflow-hidden shadow-lg">
                  <EnlargeablePhoto src={photos[1]} alt="Memory 2" className="w-full aspect-[4/3] object-cover" onClick={() => { setLightboxIndex(1); setLightboxOpen(true); }} />
                </motion.div>
              )}
            </div>

            {/* Right column: address + main body + pull-quote */}
            <motion.div className="space-y-6" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}>
              <div className="flex items-center gap-3">
                <div className="h-1 w-8 bg-amber-600/60 rounded" />
                <span className="text-xs uppercase tracking-widest text-amber-600/50 dark:text-amber-400/40 font-semibold">A Personal Note</span>
              </div>

              <div>
                <h2 className="font-serif text-2xl md:text-3xl text-amber-900 dark:text-amber-100 mb-1">{recipientName},</h2>
                <p className="text-base md:text-lg text-amber-700/65 dark:text-amber-300/65 leading-relaxed">
                  let&apos;s take a moment to shine a light on everything that makes you incredible.
                </p>
              </div>

              <div className="space-y-4">
                {messageRenderer ? (
                  <MessageBlock message={message} className="text-base md:text-lg leading-[1.75] text-amber-800 dark:text-amber-200 font-sans" messageRenderer={messageRenderer} />
                ) : (
                  (hasLongMessage ? paragraphs.slice(0, Math.ceil(paragraphs.length / 2)) : paragraphs).map((p, i) => (
                    <p key={i} className="text-base md:text-lg leading-[1.75] text-amber-800 dark:text-amber-200 font-sans">{p}</p>
                  ))
                )}
              </div>

              {/* Pull-quote */}
              <div className="border-l-4 border-amber-500/40 pl-5 py-3 my-6">
                <p className="font-serif text-xl md:text-2xl italic text-amber-900 dark:text-amber-100 leading-snug">
                  I don&apos;t say this enough, but you&apos;re one of a kind.
                </p>
              </div>

              {/* Signature */}
              <div className="pt-4 border-t border-amber-200/30 dark:border-amber-700/20">
                <p className="text-lg font-serif italic text-amber-700 dark:text-amber-300">
                  {senderLabel ? `— ${senderLabel}, your ${getSenderPerspectiveLabel(relationship)}` : `— From your ${getSenderPerspectiveLabel(relationship)}`}
                </p>
              </div>
            </motion.div>
          </div>
        </div>

        <TemplateBrandLogo />
      </div>

      {photos.length > 0 && <PhotoLightbox photos={photos} initialIndex={lightboxIndex} open={lightboxOpen} onOpenChange={setLightboxOpen} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 3) APOLOGY — Minimal Intimate Letter
//    Centered narrow column, soft textured bg, no collage, serious
// ════════════════════════════════════════════════════════════════
export function ApologyLayout({
  recipientName, relationship, message, photoUrls, publishedDate, messageRenderer, senderLabel,
}: TemplateLayoutProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const photo = photoUrls?.[0];

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Soft textured parchment background */}
      <div className="absolute inset-0 bg-gradient-to-b from-stone-100 via-stone-50 to-amber-50/30 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900" />
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
      }} />

      <div className="relative z-10 py-16 px-6">
        {/* Narrow centered letter column */}
        <div className="max-w-lg mx-auto">
          {/* Category — minimal */}
          <motion.div className="text-center mb-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <span className="text-[10px] uppercase tracking-[0.6em] text-stone-400 dark:text-slate-500 font-medium">
              An Apology
            </span>
          </motion.div>

          {/* Optional single small photo — not hero, intimate portrait */}
          {photo && (
            <motion.div
              className="mx-auto mb-10 w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden shadow-lg border-4 border-white/80 dark:border-slate-700/60"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <EnlargeablePhoto src={photo} alt="Memory" className="w-full h-full object-cover" onClick={() => setLightboxOpen(true)} />
            </motion.div>
          )}

          {/* Letter salutation */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <h2 className="font-serif text-2xl md:text-3xl text-stone-700 dark:text-stone-200 mb-2">
              Dear {recipientName},
            </h2>
            <div className="w-12 h-px bg-stone-300 dark:bg-slate-600 mb-8" />
          </motion.div>

          {/* Message body — single narrow column, generous line-height */}
          <motion.div
            className="space-y-5 mb-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <MessageBlock
              message={message}
              className="font-serif text-base md:text-lg leading-[1.9] text-stone-600 dark:text-stone-300"
              messageRenderer={messageRenderer}
            />
          </motion.div>

          {/* Closing emotional line */}
          <motion.div className="text-center mb-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
            <p className="font-serif text-xl md:text-2xl italic text-stone-500 dark:text-stone-400 leading-snug">
              I&apos;m truly, deeply sorry.
            </p>
          </motion.div>

          {/* Handwritten-style signature */}
          <motion.div className="text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}>
            <div className="w-16 h-px bg-stone-300/60 dark:bg-slate-600/40 mx-auto mb-4" />
            <p className="text-base italic text-stone-500 dark:text-stone-400 font-serif">
              {senderLabel ? `With sincere regret — ${senderLabel}` : 'With sincere regret'}
            </p>
            {publishedDate && <p className="text-xs text-stone-400/60 dark:text-slate-500/50 mt-3 italic">{publishedDate}</p>}
          </motion.div>

          <TemplateBrandLogo />
        </div>
      </div>

      {photo && <PhotoLightbox photos={[photo]} initialIndex={0} open={lightboxOpen} onOpenChange={setLightboxOpen} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 4) CLOSURE — Long-form Magazine Article (Newspaper Feature)
//    Strong title, multi-section, 2-col on desktop, images right
// ════════════════════════════════════════════════════════════════
export function ClosureLayout({
  recipientName, relationship, message, photoUrls, publishedDate, messageRenderer, senderLabel,
}: TemplateLayoutProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const photos = photoUrls?.slice(0, 3) || [];
  const paragraphs = message.split('\n\n').filter(p => p.trim());

  return (
    <div className="min-h-screen relative overflow-hidden">
      <OnceUponATimeBackground />
      <OnceUponATimeOverlays />

      <div className="relative z-10">
        {/* Newspaper masthead */}
        <div className="flex items-center justify-between px-4 md:px-8 py-2 border-b border-amber-700/20 dark:border-amber-500/15 text-[9px] uppercase tracking-[0.3em] text-amber-700/40 dark:text-amber-400/30">
          <span>Say It Now • Long Read</span>
          <span>{publishedDate || ''}</span>
        </div>

        {/* Category label */}
        <div className="text-center pt-4">
          <span className="text-xs uppercase tracking-[0.5em] text-amber-700/50 dark:text-amber-400/40 font-bold">Closure</span>
        </div>

        {/* ── Content grid: headline+text left, images right ── */}
        <div className="px-4 md:px-10 py-6">
          <div className={cn('grid gap-6', photos.length > 0 ? 'md:grid-cols-[1.6fr_1fr]' : 'max-w-4xl mx-auto')}>
            {/* Left: headline + body */}
            <div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-amber-900 dark:text-amber-100 font-black leading-[0.95] tracking-tight mb-2">
                  BEYOND
                  <br />
                  THE PAST
                </h1>
                <p className="text-sm md:text-base italic text-amber-700/55 dark:text-amber-400/50 mb-6 leading-relaxed max-w-md">
                  Telling you this was never easy, but it&apos;s time to light the way forward.
                </p>
              </motion.div>

              <div className="border-b border-amber-300/30 dark:border-amber-700/20 pb-4 mb-6">
                <h2 className="font-serif text-xl md:text-2xl text-amber-800 dark:text-amber-200">{recipientName},</h2>
              </div>

              {/* Multi-column text on desktop for long messages */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
                <div className={cn('space-y-5', paragraphs.length > 3 && 'md:columns-2 md:gap-8')}>
                  {messageRenderer ? (
                    <MessageBlock message={message} className="text-base leading-[1.8] text-amber-800/85 dark:text-amber-200/85 font-sans break-inside-avoid" messageRenderer={messageRenderer} />
                  ) : (
                    paragraphs.map((p, i) => (
                      <p key={i} className="text-base leading-[1.8] text-amber-800/85 dark:text-amber-200/85 font-sans break-inside-avoid mb-5">{p}</p>
                    ))
                  )}
                </div>
              </motion.div>

              {/* Closing emotional line */}
              <div className="pt-8 mt-6 border-t border-amber-300/25 dark:border-amber-700/20">
                <p className="font-serif text-xl italic text-amber-700 dark:text-amber-300 mb-4">
                  I&apos;m truly, deeply sorry.
                </p>
                <div className="flex items-center justify-between">
                  <p className="text-sm italic text-amber-600/60 dark:text-amber-400/50 font-serif">
                    {senderLabel ? `With peace — ${senderLabel}` : 'With peace and understanding'}
                  </p>
                  {publishedDate && <p className="text-xs text-amber-600/40 italic">{publishedDate}</p>}
                </div>
              </div>
            </div>

            {/* Right: stacked images */}
            {photos.length > 0 && (
              <div className="space-y-4 pt-0 md:pt-16">
                {photos.map((url, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.2 }} className="rounded-lg overflow-hidden shadow-xl">
                    <EnlargeablePhoto src={url} alt={`Photo ${i + 1}`} className="w-full aspect-[4/5] object-cover" onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }} />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>

        <TemplateBrandLogo />
      </div>

      {photos.length > 0 && <PhotoLightbox photos={photos} initialIndex={lightboxIndex} open={lightboxOpen} onOpenChange={setLightboxOpen} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 5) CELEBRATION — Polaroid Collage Scrapbook
//    Rotated photos, labels, energetic, layered — ONLY template
//    allowed to use polaroid/collage style
// ════════════════════════════════════════════════════════════════
export function CelebrationLayout({
  recipientName, relationship, message, photoUrls, publishedDate, messageRenderer, senderLabel,
}: TemplateLayoutProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const photos = photoUrls?.slice(0, 3) || [];
  const rotations = [-5, 4, -3];
  const labels = ['Great friend', 'Always there', 'Big Heart'];

  const confettiColors = ['#fbbf24', '#f472b6', '#34d399', '#60a5fa', '#a78bfa', '#fb923c', '#f87171'];
  const confettiShapes = ['●', '■', '▲', '★', '✦', '♦', '◆'];

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-rose-50/50 to-orange-50 dark:from-amber-950 dark:via-rose-950/30 dark:to-orange-950" />
      <div className="absolute inset-0 opacity-[0.05]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
      }} />

      {/* 🎉 Confetti rain animation */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[3]">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={`confetti-${i}`}
            className="absolute"
            style={{
              left: `${(i * 3.4) % 100}%`,
              top: '-3%',
              color: confettiColors[i % confettiColors.length],
              fontSize: `${8 + (i % 5) * 3}px`,
              opacity: 0.5,
            }}
            animate={{
              y: [0, 900],
              rotate: [0, 360 * (i % 2 === 0 ? 1 : -1)],
              x: [0, (i % 2 === 0 ? 1 : -1) * (20 + i % 30)],
              opacity: [0, 0.6, 0.4, 0],
            }}
            transition={{
              duration: 5 + i * 0.3,
              repeat: Infinity,
              delay: i * 0.4,
              ease: 'linear',
            }}
          >
            {confettiShapes[i % confettiShapes.length]}
          </motion.div>
        ))}
      </div>

      {/* 🎈 Floating balloons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[2]">
        {['🎈', '🎁', '🎊', '🎉', '🎈', '🎁'].map((emoji, i) => (
          <motion.div
            key={`balloon-${i}`}
            className="absolute"
            style={{
              left: `${8 + i * 16}%`,
              bottom: '-8%',
              fontSize: `${24 + (i % 3) * 8}px`,
              opacity: 0.3,
            }}
            animate={{
              y: [0, -700],
              x: [0, (i % 2 === 0 ? 15 : -15)],
              opacity: [0, 0.4, 0.3, 0],
              rotate: [0, i % 2 === 0 ? 10 : -10],
            }}
            transition={{
              duration: 12 + i * 2,
              repeat: Infinity,
              delay: i * 2.5,
              ease: 'easeOut',
            }}
          >
            {emoji}
          </motion.div>
        ))}
      </div>

      {/* Radial burst background glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_30%,rgba(251,191,36,0.15),transparent_60%)] pointer-events-none" />

      <div className="relative z-10 py-8 px-4 md:px-8">
        <motion.div className="text-center mb-3" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <span className="text-xs uppercase tracking-[0.5em] text-amber-700/50 dark:text-amber-400/40 font-bold">🎉 Celebration 🎉</span>
        </motion.div>

        <motion.div className="text-center mb-10" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}>
          <h1 className="font-heading text-2xl sm:text-4xl md:text-6xl text-amber-800 dark:text-amber-200 italic">
            Happy Celebration
          </h1>
          <p className="text-base text-amber-600/60 dark:text-amber-400/50 mt-2">For {recipientName}</p>
        </motion.div>

        {/* Polaroid collage */}
        {photos.length > 0 && (
          <div className="flex flex-wrap justify-center gap-4 md:gap-6 mb-10 max-w-3xl mx-auto">
            {photos.map((url, i) => (
              <motion.div
                key={i}
                className="relative bg-white dark:bg-slate-800 p-2 pb-10 shadow-xl cursor-pointer"
                style={{ transform: `rotate(${rotations[i % rotations.length]}deg)` }}
                initial={{ opacity: 0, y: 40, rotate: rotations[i % rotations.length] * 2 }}
                animate={{ opacity: 1, y: 0, rotate: rotations[i % rotations.length] }}
                transition={{ delay: 0.3 + i * 0.15, type: 'spring', stiffness: 150 }}
                whileHover={{ scale: 1.06, rotate: 0, zIndex: 10 }}
              >
                <EnlargeablePhoto src={url} alt={`Memory ${i + 1}`} className="w-28 h-28 md:w-40 md:h-40 object-cover" onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }} />
                <span className="absolute bottom-2 left-0 right-0 text-center text-xs md:text-sm font-serif italic text-slate-600 dark:text-slate-300">{labels[i % labels.length]}</span>
              </motion.div>
            ))}
          </div>
        )}

        <motion.div className="max-w-2xl mx-auto text-center mb-10 space-y-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
          <MessageBlock message={message} className="text-base md:text-lg leading-[1.75] text-amber-800 dark:text-amber-200 font-sans" messageRenderer={messageRenderer} />
        </motion.div>

        <motion.div className="text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
          <div className="w-20 h-px bg-amber-400/40 mx-auto mb-4" />
          <p className="text-lg italic font-serif text-amber-700 dark:text-amber-300">
            {senderLabel ? `With joy — ${senderLabel}` : `With joy, from your ${getSenderPerspectiveLabel(relationship)}`}
          </p>
          {publishedDate && <p className="text-xs text-amber-600/40 mt-2">{publishedDate}</p>}
        </motion.div>

        <TemplateBrandLogo />
      </div>

      {photos.length > 0 && <PhotoLightbox photos={photos} initialIndex={lightboxIndex} open={lightboxOpen} onOpenChange={setLightboxOpen} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 6) ANNIVERSARY — Luxury Centered Frame + Gold Editorial
//    Double ornamental border, centered framed photo, serif+script
// ════════════════════════════════════════════════════════════════
export function AnniversaryLayout({
  recipientName, relationship, message, photoUrls, publishedDate, messageRenderer, senderLabel,
}: TemplateLayoutProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const photos = photoUrls?.slice(0, 3) || [];

  return (
    <div className="min-h-screen relative overflow-hidden">
      <ForeverInMyHeartBackground />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(251,191,36,0.12),transparent_60%)] pointer-events-none z-[1]" />
      {/* Ornamental double border */}
      <div className="absolute inset-3 md:inset-6 border border-amber-300/25 dark:border-amber-600/15 pointer-events-none z-[2]" />
      <div className="absolute inset-5 md:inset-8 border border-amber-300/12 dark:border-amber-600/8 pointer-events-none z-[2]" />

      {/* Floating hearts animation */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[3]">
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={`heart-${i}`}
            className="absolute text-rose-400/20 dark:text-rose-300/15"
            style={{
              left: `${5 + (i * 8.3) % 90}%`,
              bottom: '-5%',
              fontSize: `${14 + (i % 4) * 6}px`,
            }}
            animate={{
              y: [0, -800],
              opacity: [0, 0.6, 0.4, 0],
              rotate: [0, (i % 2 === 0 ? 1 : -1) * 20],
              x: [0, (i % 2 === 0 ? 1 : -1) * 30],
            }}
            transition={{
              duration: 8 + i * 0.7,
              repeat: Infinity,
              delay: i * 1.2,
              ease: 'easeOut',
            }}
          >
            ♥
          </motion.div>
        ))}
      </div>

      {/* Sparkle ring accents */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[3]">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={`ring-${i}`}
            className="absolute"
            style={{
              left: `${10 + (i * 12) % 80}%`,
              top: `${8 + (i * 11) % 84}%`,
            }}
            animate={{
              opacity: [0, 0.5, 0],
              scale: [0.5, 1.2, 0.5],
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              delay: i * 0.8,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(251,191,36,0.35)" strokeWidth="1.5">
              <circle cx="12" cy="12" r="8" />
              <circle cx="12" cy="12" r="4" />
            </svg>
          </motion.div>
        ))}
      </div>

      <div className="relative z-10 py-14 px-6 md:px-14">
        <div className="max-w-3xl mx-auto text-center">
          <motion.span className="text-[9px] uppercase tracking-[0.6em] text-amber-600/40 dark:text-amber-400/30 font-medium" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            Anniversary
          </motion.span>

          {/* Gold divider with heart */}
          <motion.div className="flex items-center justify-center gap-6 my-6" initial={{ opacity: 0, scaleX: 0.5 }} animate={{ opacity: 1, scaleX: 1 }} transition={{ delay: 0.2 }}>
            <div className="w-16 md:w-24 h-px bg-gradient-to-r from-transparent to-amber-400/50" />
            <motion.div
              animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Heart className="h-6 w-6 text-rose-500/60 dark:text-rose-400/50" fill="currentColor" />
            </motion.div>
            <div className="w-16 md:w-24 h-px bg-gradient-to-l from-transparent to-amber-400/50" />
          </motion.div>

          <motion.h1 className="font-serif text-2xl sm:text-4xl md:text-5xl text-amber-900 dark:text-amber-100 tracking-wide mb-2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            Happy Anniversary
          </motion.h1>
          <motion.p className="text-lg font-serif italic text-amber-700/60 dark:text-amber-300/50 mb-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
            For {recipientName}
          </motion.p>

          {/* Photos — show all uploaded */}
          {photos.length > 0 && (
            <motion.div className="flex justify-center gap-4 mb-14 flex-wrap" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
              {photos.map((url, i) => (
                <motion.div
                  key={i}
                  className="p-2 md:p-3 bg-gradient-to-br from-amber-100/70 to-amber-200/30 dark:from-amber-900/30 dark:to-amber-800/15 rounded-sm shadow-2xl"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + i * 0.15 }}
                >
                  <div className="p-1 border border-amber-300/35 dark:border-amber-600/25">
                    <EnlargeablePhoto src={url} alt={`Photo ${i + 1}`} className="w-32 h-32 md:w-44 md:h-44 object-cover" onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }} />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Message — centered serif */}
          <motion.div className="max-w-2xl mx-auto mb-14 space-y-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
            <MessageBlock message={message} className="font-serif text-base md:text-lg leading-[1.85] text-amber-800 dark:text-amber-200" messageRenderer={messageRenderer} />
          </motion.div>

          {/* Elegant signature */}
          <motion.div className="space-y-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="w-12 h-px bg-amber-400/25" />
              <div className="w-2 h-2 rounded-full bg-amber-400/25" />
              <div className="w-12 h-px bg-amber-400/25" />
            </div>
            <p className="text-xl font-serif italic text-amber-700 dark:text-amber-300">
              {senderLabel ? `With all our love, ${senderLabel}` : 'With all our love'}
            </p>
            {publishedDate && <p className="text-sm text-amber-600/40 italic">{publishedDate}</p>}
          </motion.div>

          <TemplateBrandLogo />
        </div>
      </div>

      {photos.length > 0 && <PhotoLightbox photos={photos} initialIndex={lightboxIndex} open={lightboxOpen} onOpenChange={setLightboxOpen} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 7) BIRTHDAY — Editorial Celebration Cover
//    Bold graphic headline, confetti accents, strong hierarchy
//    NOT a collage — uses single hero or no image
// ════════════════════════════════════════════════════════════════
export function BirthdayLayout({
  recipientName, relationship, message, photoUrls, publishedDate, messageRenderer, senderLabel,
}: TemplateLayoutProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const photos = photoUrls?.slice(0, 3) || [];

  const birthdayEmojis = ['🎂', '🎁', '🎈', '🎉', '🎊', '🧁', '🎂', '🎁', '🎈', '🎉'];
  const confettiColors = ['#c084fc', '#38bdf8', '#fbbf24', '#fb7185', '#34d399', '#f472b6', '#a78bfa'];

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Vibrant gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-sky-50 to-amber-50 dark:from-violet-950 dark:via-sky-950/50 dark:to-amber-950/30" />

      {/* Confetti rain */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[2]">
        {[...Array(25)].map((_, i) => (
          <motion.div
            key={`confetti-${i}`}
            className="absolute"
            style={{
              left: `${(i * 4.1) % 100}%`,
              top: '-3%',
              width: 4 + (i % 4) * 3,
              height: 4 + (i % 4) * 3,
              backgroundColor: confettiColors[i % confettiColors.length],
              borderRadius: i % 3 === 0 ? '50%' : i % 3 === 1 ? '2px' : '0',
              transform: i % 3 === 2 ? 'rotate(45deg)' : 'none',
            }}
            animate={{
              y: [0, 800],
              rotate: [0, 360 * (i % 2 === 0 ? 1 : -1)],
              x: [0, (i % 2 === 0 ? 1 : -1) * 25],
              opacity: [0, 0.6, 0.4, 0],
            }}
            transition={{
              duration: 5 + i * 0.3,
              repeat: Infinity,
              delay: i * 0.35,
              ease: 'linear',
            }}
          />
        ))}
      </div>

      {/* 🎈 Floating birthday emojis */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[3]">
        {birthdayEmojis.map((emoji, i) => (
          <motion.div
            key={`bday-${i}`}
            className="absolute"
            style={{
              left: `${5 + i * 10}%`,
              bottom: '-8%',
              fontSize: `${20 + (i % 3) * 8}px`,
              opacity: 0.3,
            }}
            animate={{
              y: [0, -650],
              x: [0, (i % 2 === 0 ? 12 : -12)],
              opacity: [0, 0.4, 0.3, 0],
              rotate: [0, i % 2 === 0 ? 15 : -15],
            }}
            transition={{
              duration: 10 + i * 1.5,
              repeat: Infinity,
              delay: i * 1.8,
              ease: 'easeOut',
            }}
          >
            {emoji}
          </motion.div>
        ))}
      </div>

      {/* Radial celebration glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_20%,rgba(192,132,252,0.12),transparent_55%)] pointer-events-none" />

      <div className="relative z-10 py-10 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          {/* Bold graphic headline */}
          <motion.div className="mb-8" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-2 mb-4">
              <motion.span animate={{ rotate: [0, 15, -15, 0] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}>🎉</motion.span>
              <span className="text-[10px] uppercase tracking-[0.5em] text-violet-600/50 dark:text-violet-400/40 font-bold">Birthday</span>
              <motion.span animate={{ rotate: [0, -15, 15, 0] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0.8 }}>🎂</motion.span>
            </div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-slate-800 dark:text-slate-100 font-black leading-[0.9] tracking-tight">
              HAPPY
              <br />
              <motion.span
                className="text-violet-600 dark:text-violet-400 inline-block"
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              >
                BIRTHDAY
              </motion.span>
            </h1>
            <p className="text-xl md:text-2xl font-serif italic text-slate-600 dark:text-slate-300 mt-4">
              Dear {recipientName} 🎈
            </p>
          </motion.div>

          {/* Photos — show all uploaded */}
          {photos.length > 0 && (
            <div className="flex justify-center gap-4 mb-10 flex-wrap">
              {photos.length === 1 ? (
                <motion.div
                  className="rounded-2xl overflow-hidden shadow-2xl w-full"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <EnlargeablePhoto src={photos[0]} alt="Birthday memory" className="w-full aspect-[16/10] object-cover" onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }} />
                </motion.div>
              ) : (
                photos.map((url, i) => (
                  <motion.div
                    key={i}
                    className="rounded-xl overflow-hidden shadow-xl border-2 border-white/60 dark:border-slate-700/40"
                    initial={{ opacity: 0, scale: 0.9, rotate: (i % 2 === 0 ? -3 : 3) }}
                    animate={{ opacity: 1, scale: 1, rotate: (i % 2 === 0 ? -2 : 2) }}
                    transition={{ delay: 0.3 + i * 0.15, type: 'spring' }}
                    whileHover={{ scale: 1.05, rotate: 0 }}
                  >
                    <EnlargeablePhoto src={url} alt={`Birthday ${i + 1}`} className="w-28 h-28 md:w-40 md:h-40 object-cover" onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }} />
                  </motion.div>
                ))
              )}
            </div>
          )}

          {/* Message — left-aligned, editorial */}
          <motion.div className="space-y-5 mb-12" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
            <MessageBlock
              message={message}
              className="text-base md:text-lg leading-[1.8] text-slate-700 dark:text-slate-200 font-sans"
              messageRenderer={messageRenderer}
            />
          </motion.div>

          {/* Signature */}
          <motion.div className="flex items-center gap-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
            <div className="h-px flex-1 bg-gradient-to-r from-violet-300/30 to-transparent" />
            <div className="text-center">
              <p className="text-lg italic font-serif text-violet-600 dark:text-violet-400">
                {senderLabel ? `With celebration — ${senderLabel}` : `With love and celebration`}
              </p>
              {publishedDate && <p className="text-xs text-slate-400 mt-2">{publishedDate}</p>}
            </div>
            <div className="h-px flex-1 bg-gradient-to-l from-violet-300/30 to-transparent" />
          </motion.div>

          <TemplateBrandLogo />
        </div>
      </div>

      {photos.length > 0 && <PhotoLightbox photos={photos} initialIndex={lightboxIndex} open={lightboxOpen} onOpenChange={setLightboxOpen} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 8) CUSTOM — Clean Modern Editorial
//    Flexible for any message, balanced image+text composition
// ════════════════════════════════════════════════════════════════
function CustomFloatingParticles({ elements }: { elements: FloatingElement[] }) {
  const activeElements = elements.filter(e => e.enabled);
  if (activeElements.length === 0) return null;
  const emojiMap: Record<string, string> = { hearts: '♥', suns: '☀', stars: '★', smileys: '☺' };
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-5">
      {activeElements.flatMap((el, elIdx) =>
        Array.from({ length: 14 }).map((_, i) => (
          <span key={`${el.type}-${i}`} className="absolute animate-float-up opacity-50" style={{
            color: el.color, left: `${3 + (i * 7) % 94}%`, top: `${5 + (i * 7.5) % 90}%`,
            animationDelay: `${i * 0.9 + elIdx * 0.5}s`, animationDuration: `${5 + i * 0.7}s`, fontSize: `${16 + (i % 4) * 5}px`,
          }}>
            {emojiMap[el.type]}
          </span>
        ))
      )}
    </div>
  );
}

export function CustomLayout({
  recipientName, relationship, message, photoUrls, publishedDate, customSettings, messageRenderer, senderLabel,
}: TemplateLayoutProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const photos = photoUrls || [];
  const cs = customSettings;
  const bgIdx = cs?.backgroundPhotoIndex ?? null;
  const backdropUrl = bgIdx !== null ? photos[bgIdx] : null;
  const inlinePhotos = backdropUrl ? photos.filter((_, i) => i !== bgIdx).slice(0, 3) : photos.slice(0, 3);
  const typoPreset = cs?.typographyPreset ?? 'classic';
  const titleBold = cs?.titleBold !== false;
  const textAlign = cs?.textAlignment === 'left' ? 'text-left' : 'text-center';
  const justifyClass = cs?.textAlignment === 'left' ? '' : 'justify-center';
  const mxClass = cs?.textAlignment === 'left' ? '' : 'mx-auto';
  const titleFontClass = typoPreset === 'modern' ? 'font-sans' : typoPreset === 'handwritten' ? 'italic font-serif' : 'font-serif';
  const bodyFontClass = typoPreset === 'modern' ? 'font-sans' : typoPreset === 'handwritten' ? 'italic font-serif' : 'font-sans';
  const photoLayout = cs?.photoLayout ?? 'circular';

  // Use custom colors from settings
  const fontColor = cs?.fontColor || '#1a1a2e';
  const bgColor = cs?.backgroundColor || '#fdf6e3';

  const renderPhotos = () => {
    if (inlinePhotos.length === 0) return null;
    switch (photoLayout) {
      case 'grid':
        return (
          <div className={cn('grid gap-3 mb-10', inlinePhotos.length === 1 ? 'grid-cols-1 max-w-sm' : inlinePhotos.length === 2 ? 'grid-cols-2 max-w-lg' : 'grid-cols-3 max-w-2xl', mxClass)}>
            {inlinePhotos.map((url, i) => (
              <EnlargeablePhoto key={i} src={url} alt={`Photo ${i + 1}`}
                className="w-full aspect-square rounded-xl overflow-hidden shadow-lg object-cover"
                onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
              />
            ))}
          </div>
        );
      case 'collage':
        return (
          <div className={cn('relative mb-10 max-w-lg h-64 md:h-80', mxClass)}>
            {inlinePhotos.map((url, i) => {
              const transforms = [
                'rotate-[-4deg] top-0 left-4',
                'rotate-[3deg] top-4 right-4',
                'rotate-[-2deg] bottom-0 left-1/4',
              ];
              return (
                <motion.div
                  key={i}
                  className={cn('absolute w-40 h-40 md:w-48 md:h-48 rounded-lg overflow-hidden shadow-xl border-4 border-white', transforms[i] || '')}
                  initial={{ opacity: 0, scale: 0.8, rotate: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.15 }}
                >
                  <EnlargeablePhoto src={url} alt={`Photo ${i + 1}`}
                    className="w-full h-full object-cover"
                    onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
                  />
                </motion.div>
              );
            })}
          </div>
        );
      case 'strip':
        return (
          <div className={cn('flex gap-2 mb-10 overflow-hidden', justifyClass)}>
            {inlinePhotos.map((url, i) => (
              <EnlargeablePhoto key={i} src={url} alt={`Photo ${i + 1}`}
                className="w-32 h-44 md:w-40 md:h-56 rounded-lg overflow-hidden shadow-lg object-cover flex-shrink-0"
                onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
              />
            ))}
          </div>
        );
      default: // circular
        return (
          <div className={cn('flex gap-4 mb-10', justifyClass)}>
            {inlinePhotos.map((url, i) => (
              <EnlargeablePhoto key={i} src={url} alt={`Photo ${i + 1}`}
                className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden shadow-lg border-2 border-white/60"
                onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
              />
            ))}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: backdropUrl ? undefined : bgColor }}>
      {backdropUrl ? (
        <div className="absolute inset-0 z-0">
          {(() => { const bp = getBackdropPreset(typoPreset); return (<>
            <img src={backdropUrl} alt="" className={cn("w-full h-full object-cover", bp.imageBlur, bp.imageOpacity)} />
            <div className={cn("absolute inset-0 bg-gradient-to-b", cs?.autoContrast !== false ? bp.overlayOn : bp.overlayOff)} />
          </>); })()}
        </div>
      ) : null}
      {cs?.floatingElements && <CustomFloatingParticles elements={cs.floatingElements} />}

      <div className={cn("relative z-10 py-20 px-4", textAlign)}>
        <div className={cn("container max-w-4xl", mxClass)}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-sm uppercase tracking-widest mb-4" style={{ color: fontColor, opacity: 0.6 }}>A Personal Message</p>
            <h1
              className={cn(titleFontClass, 'text-3xl md:text-4xl mb-4', !titleBold && 'font-normal')}
              style={{ color: fontColor }}
            >
              For {recipientName}
            </h1>
            <p className="text-lg mb-8" style={{ color: fontColor, opacity: 0.6 }}>
              {senderLabel ? `From ${senderLabel}, your ${getSenderPerspectiveLabel(relationship)}` : `From your ${getSenderPerspectiveLabel(relationship)}`}
            </p>

            {renderPhotos()}

            <div className={cn('max-w-2xl mb-10 space-y-4', mxClass)}>
              <MessageBlock
                message={message}
                className={cn(bodyFontClass, 'text-base md:text-xl leading-[1.75]')}
                messageRenderer={messageRenderer}
              />
            </div>

            <div className={cn('flex items-center gap-2', justifyClass)} style={{ color: fontColor }}>
              <Heart className="h-4 w-4" fill="currentColor" />
              <span className="font-medium">With heartfelt sincerity</span>
            </div>

            {publishedDate && <p className="text-sm mt-8" style={{ color: fontColor, opacity: 0.4 }}>Shared on {publishedDate}</p>}
            <TemplateBrandLogo />
          </motion.div>
        </div>
      </div>

      {/* Apply font color to message block via style override */}
      <style>{`.min-h-screen [class*="space-y-4"] p { color: ${fontColor} !important; }`}</style>

      {photos.length > 0 && <PhotoLightbox photos={photos} initialIndex={lightboxIndex} open={lightboxOpen} onOpenChange={setLightboxOpen} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 9) CRUSH / SECRET ADMIRATION — Mysterious Dark Note
//    Dark/dreamy palette, intimate, soft reveal, anonymous feel
// ════════════════════════════════════════════════════════════════
export function CrushLayout({
  recipientName, relationship, message, photoUrls, publishedDate, messageRenderer, senderLabel,
}: TemplateLayoutProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const photo = photoUrls?.[0];

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Dark dreamy gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-purple-950/80 to-slate-950" />
      {/* Soft light bokeh */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/3 w-64 h-64 rounded-full bg-purple-500/8 blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-48 h-48 rounded-full bg-rose-500/6 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full bg-violet-400/5 blur-3xl" />
      </div>

      <div className="relative z-10 py-16 px-6">
        <div className="max-w-md mx-auto text-center">
          {/* Anonymous icon */}
          <motion.div className="mb-8" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-500/10 border border-purple-400/20 mb-4">
              <Lock className="h-7 w-7 text-purple-400/60" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.6em] text-purple-400/40 font-medium">
              Secret Admiration
            </p>
          </motion.div>

          {/* Headline */}
          <motion.h1
            className="font-serif text-3xl md:text-4xl text-purple-100/90 italic leading-tight mb-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Someone has something
            <br />to tell you…
          </motion.h1>

          <motion.p
            className="text-purple-300/40 text-sm mb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            A message for {recipientName}
          </motion.p>

          {/* Optional photo — small, circular, mysterious */}
          {photo && (
            <motion.div
              className="mx-auto mb-10 w-28 h-28 rounded-full overflow-hidden shadow-2xl ring-2 ring-purple-400/20"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
            >
              <EnlargeablePhoto src={photo} alt="Memory" className="w-full h-full object-cover" onClick={() => setLightboxOpen(true)} />
            </motion.div>
          )}

          {/* Message — centered, soft glow text */}
          <motion.div
            className="space-y-5 mb-14"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <MessageBlock
              message={message}
              className="text-base md:text-lg leading-[1.85] text-purple-100/70 font-sans"
              messageRenderer={messageRenderer}
            />
          </motion.div>

          {/* Anonymous signature */}
          <motion.div
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
          >
            <div className="w-12 h-px bg-purple-400/20 mx-auto mb-4" />
            <p className="text-base italic text-purple-300/50 font-serif">
              {senderLabel ? senderLabel : '— From someone who cares'}
            </p>
            {publishedDate && <p className="text-xs text-purple-400/25 mt-3">{publishedDate}</p>}
          </motion.div>

          <TemplateBrandLogo />
        </div>
      </div>

      {photo && <PhotoLightbox photos={[photo]} initialIndex={0} open={lightboxOpen} onOpenChange={setLightboxOpen} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// HARD CONVERSATION — Centered honest note (uses distinct structure)
// ════════════════════════════════════════════════════════════════
export function HardConversationLayout({
  recipientName, relationship, message, photoUrls, publishedDate, messageRenderer, senderLabel,
}: TemplateLayoutProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const photos = photoUrls?.slice(0, 3) || [];

  return (
    <div className="min-h-screen relative overflow-hidden">
      <YourStoryMattersBackground />
      <YourStoryMattersOverlays />

      <div className="relative z-10 py-16 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-[10px] uppercase tracking-[0.4em] text-slate-500 dark:text-slate-400 font-medium">
              Something I Need to Share
            </span>

            <h1 className="font-serif text-3xl md:text-4xl text-slate-800 dark:text-slate-100 mt-4 mb-2">
              To {recipientName}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-8">With honesty and respect</p>

            {photos.length > 0 && (
              <div className="flex justify-center gap-4 mb-10">
                {photos.map((url, i) => (
                  <EnlargeablePhoto key={i} src={url} alt={`Photo ${i + 1}`}
                    className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden shadow-lg border-2 border-white/60"
                    onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
                  />
                ))}
              </div>
            )}

            <div className="max-w-2xl mx-auto mb-10 space-y-4">
              <MessageBlock message={message} className="text-base md:text-lg leading-[1.75] text-slate-700 dark:text-slate-200 font-sans" messageRenderer={messageRenderer} />
            </div>

            <div className="flex items-center justify-center gap-2 text-primary">
              <Heart className="h-4 w-4" fill="currentColor" />
              <span className="font-medium text-sm">With care{senderLabel ? ` — ${senderLabel}` : ''}</span>
            </div>

            {publishedDate && <p className="text-sm mt-8 text-slate-400 dark:text-slate-500">Shared on {publishedDate}</p>}

            <TemplateBrandLogo />
          </motion.div>
        </div>
      </div>

      {photos.length > 0 && <PhotoLightbox photos={photos} initialIndex={lightboxIndex} open={lightboxOpen} onOpenChange={setLightboxOpen} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 10) ENCOURAGEMENT — Bright Rallying Cry
//     Emerald/cyan gradients, chibi display, motivational energy
// ════════════════════════════════════════════════════════════════
export function EncouragementLayout({
  recipientName, relationship, message, photoUrls, publishedDate, messageRenderer, senderLabel, metadata,
}: TemplateLayoutProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const photos = photoUrls?.slice(0, 3) || [];
  const chibiUrl = metadata?.chibiUrl;

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-emerald-950 via-teal-900 to-cyan-950">
      {/* Animated background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full bg-emerald-500/10 blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-cyan-500/10 blur-3xl"
          animate={{ scale: [1.1, 0.9, 1.1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      <div className="relative z-10">
        {/* Category badge */}
        <div className="flex justify-center pt-8">
          <motion.span
            className="text-[10px] uppercase tracking-[0.5em] text-emerald-400/60 font-medium border border-emerald-400/20 px-4 py-1.5 rounded-full"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            ✨ Encouragement
          </motion.span>
        </div>

        {/* Giant headline */}
        <motion.div
          className="text-center px-6 md:px-12 pt-8 pb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white leading-[0.95] tracking-tight font-black">
            YOU GOT
            <br />
            <span className="bg-gradient-to-r from-emerald-300 via-cyan-300 to-teal-300 bg-clip-text text-transparent">
              THIS
            </span>
          </h1>
          <p className="text-emerald-300/50 text-sm mt-4 font-sans uppercase tracking-widest">
            A message for {recipientName}
          </p>
        </motion.div>

        {/* Photos */}
        {photos.length > 0 && (
          <motion.div
            className="flex justify-center gap-4 px-6 py-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            {photos.map((url, i) => (
              <EnlargeablePhoto
                key={i}
                src={url}
                alt={`Photo ${i + 1}`}
                className="w-24 h-24 md:w-32 md:h-32 rounded-2xl object-cover shadow-xl border-2 border-emerald-400/20"
                onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
              />
            ))}
          </motion.div>
        )}

        {/* Chibi display */}
        {chibiUrl && (
          <motion.div
            className="flex justify-center px-6 py-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
          >
            <img
              src={chibiUrl}
              alt="Your encouragement chibis"
              className="rounded-2xl shadow-2xl max-w-[300px] md:max-w-[360px] border-2 border-emerald-400/20"
            />
          </motion.div>
        )}

        {/* Divider */}
        <div className="flex justify-center py-4">
          <div className="w-20 h-px bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent" />
        </div>

        {/* Message body */}
        <motion.div
          className="max-w-2xl mx-auto px-6 md:px-12 pb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <div className="space-y-5">
            <MessageBlock
              message={message}
              className="text-base md:text-lg leading-[1.85] text-emerald-100/80 font-sans"
              messageRenderer={messageRenderer}
            />
          </div>

          {/* Pull quote */}
          <div className="border-l-4 border-emerald-400/40 pl-5 py-3 my-8">
            <p className="font-serif text-xl md:text-2xl italic text-emerald-200 leading-snug">
              I see how hard you&apos;ve been working, and I want you to know — it matters.
            </p>
          </div>

          {/* Signature */}
          <motion.div
            className="text-center pt-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <div className="w-16 h-px bg-emerald-400/30 mx-auto mb-4" />
            <p className="text-lg italic font-serif text-emerald-300/70">
              {senderLabel ? `Believing in you always — ${senderLabel}` : 'Believing in you always'}
            </p>
            {publishedDate && <p className="text-sm mt-2 text-emerald-400/30">{publishedDate}</p>}
          </motion.div>

          {/* Floating emoji decorations */}
          <div className="flex justify-center gap-3 pt-6">
            {['✨', '⭐', '💪', '🌟'].map((emoji, i) => (
              <motion.span
                key={i}
                className="text-2xl"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 2, delay: i * 0.3, repeat: Infinity }}
              >
                {emoji}
              </motion.span>
            ))}
          </div>

          <TemplateBrandLogo />
        </motion.div>
      </div>

      {photos.length > 0 && <PhotoLightbox photos={photos} initialIndex={lightboxIndex} open={lightboxOpen} onOpenChange={setLightboxOpen} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// Template renderer — maps template type to layout component
// ════════════════════════════════════════════════════════════════
export function renderTemplateLayout(
  template: TemplateType | string,
  props: TemplateLayoutProps
) {
  const templateMap: Record<string, () => JSX.Element> = {
    love: () => <LoveLayout {...props} />,
    gratitude: () => <GratitudeLayout {...props} />,
    apology: () => <ApologyLayout {...props} />,
    closure: () => <ClosureLayout {...props} />,
    celebration: () => <CelebrationLayout {...props} />,
    anniversary: () => <AnniversaryLayout {...props} />,
    birthday: () => <BirthdayLayout {...props} />,
    custom: () => <CustomLayout {...props} />,
    hard_conversation: () => <HardConversationLayout {...props} />,
    encouragement: () => <EncouragementLayout {...props} />,
    // Legacy mappings
    keepsake: () => <GratitudeLayout {...props} />,
    letter: () => <GratitudeLayout {...props} />,
    headline: () => <LoveLayout {...props} />,
    moment: () => <LoveLayout {...props} />,
    reflection: () => <ClosureLayout {...props} />,
    classic: () => <GratitudeLayout {...props} />,
    modern: () => <LoveLayout {...props} />,
    bold: () => <HardConversationLayout {...props} />,
    journal: () => <ClosureLayout {...props} />,
  };

  const renderer = templateMap[template] || templateMap.gratitude;
  return renderer();
}
